<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container-custom py-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900 mb-4">
          {{ $t('menu.title') }}
        </h1>
        
        <!-- Search and Filter -->
        <div class="flex flex-col sm:flex-row gap-4">
          <div class="relative flex-1">
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('menu.searchPlaceholder')"
              class="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              @input="debouncedSearch"
            >
            <MagnifyingGlassIcon class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          
          <select
            v-model="selectedCategory"
            class="px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">{{ $t('menu.allCategories') }}</option>
            <option v-for="cat in categories" :key="cat.id" :value="cat.id">
              {{ cat.name }}
            </option>
          </select>
        </div>
      </div>
      
      <!-- Loading -->
      <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div v-for="i in 8" :key="i" class="skeleton h-80 rounded-lg" />
      </div>
      
      <!-- Products Grid -->
      <div v-else-if="products.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ProductCard
          v-for="product in products"
          :key="product.id"
          :product="product"
        />
      </div>
      
      <!-- Empty State -->
      <div v-else class="text-center py-16">
        <ShoppingBagIcon class="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          {{ $t('menu.noProducts') }}
        </h3>
        <p class="text-gray-500">
          Tente ajustar seus filtros de busca
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { MagnifyingGlassIcon, ShoppingBagIcon } from '@heroicons/vue/24/outline'
import { useCompanyStore } from '@/stores'
import { categoryApi, productApi } from '@/utils/api'
import type { Category, Product } from '@/types'
import ProductCard from '@/components/products/ProductCard.vue'

const route = useRoute()
const companyStore = useCompanyStore()

const searchQuery = ref('')
const selectedCategory = ref('')
const categories = ref<Category[]>([])
const products = ref<Product[]>([])
const loading = ref(true)

let searchTimeout: ReturnType<typeof setTimeout>

const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    loadProducts()
  }, 300)
}

const loadCategories = async () => {
  if (!companyStore.company?.id) return
  const companyId = companyStore.company.id
  
  try {
    const res = await categoryApi.getByCompany(companyId)
    categories.value = res.data
  } catch (error) {
    console.error('Erro ao carregar categorias:', error)
  }
}

const loadProducts = async () => {
  if (!companyStore.company?.id) {
    loading.value = false
    return
  }
  const companyId = companyStore.company.id
  
  loading.value = true
  try {
    const params: any = {
      search: searchQuery.value || undefined,
      category_id: selectedCategory.value || undefined,
      available_only: true
    }
    
    const res = await productApi.getByCompany(companyId, params)
    products.value = res.data
  } catch (error) {
    console.error('Erro ao carregar produtos:', error)
  } finally {
    loading.value = false
  }
}

// Watch for query changes
watch(() => route.query.search, (newSearch) => {
  if (newSearch) {
    searchQuery.value = newSearch as string
    loadProducts()
  }
}, { immediate: true })

watch(() => route.query.category, (newCategory) => {
  if (newCategory) {
    selectedCategory.value = String(newCategory)
    loadProducts()
  } else {
    selectedCategory.value = ''
    loadProducts()
  }
}, { immediate: true })

watch(selectedCategory, () => {
  loadProducts()
})

watch(() => companyStore.company?.id, (newId) => {
  if (newId) {
    loadCategories()
    loadProducts()
  }
}, { immediate: true })
</script>
